// frontend/src/features/livestock/framing.ts
// Tank shot framing (docs/plans/2026-10-29-tank-shot-framing-design.md, 4 and 7):
// a crop in fractions of the photo's width and height, which may reach past its
// edges, and a rotation in degrees (clockwise, about the crop's centre). The
// maths for drawing a framed shot, and for the Line up editor's pan and zoom.
//
// The editor works through the crop's centre c and rotation r: the point (u, v)
// of the frame (0..1 each way) shows the photo point
//   c + R(-r) · ((u - 0.5) · w · W, (v - 0.5) · h · H)
// in photo pixels, with H = 1 and W = the photo's width / height. Pan and zoom
// solve that for a new centre; rotation keeps the centre.

import type { CSSProperties } from 'react';
import type { TankPhotoFraming } from '@/types/tankPhotos';

export const MIN_SIZE = 0.05;
export const MAX_SIZE = 4;
export const MAX_ROTATION = 20;

const DECIMALS = 1e4;
const round = (value: number): number => Math.round(value * DECIMALS) / DECIMALS;
const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Width / height of the shot as framed, or of the photo when it isn't. */
export function framedRatio(
  photoRatio: number,
  framing: TankPhotoFraming | null | undefined
): number {
  return framing ? (photoRatio * framing.w) / framing.h : photoRatio;
}

/**
 * Style for an <img> inside a box of the framed shape that clips (overflow
 * hidden): the whole photo, scaled and placed so the crop fills the box.
 * Percentages only, so it fits the box at any size.
 */
export function framedImageStyle(framing: TankPhotoFraming): CSSProperties {
  const { x, y, w, h, rotation } = framing;
  return {
    position: 'absolute',
    left: `${(-x / w) * 100}%`,
    top: `${(-y / h) * 100}%`,
    width: `${100 / w}%`,
    height: `${100 / h}%`,
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'fill',
    transformOrigin: `${(x + w / 2) * 100}% ${(y + h / 2) * 100}%`,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
  };
}

/** The largest centred crop of `frameRatio`'s shape within the photo: where Line up starts. */
export function initialFit(
  photoRatio: number,
  frameRatio: number,
  refPhotoId: number | null
): TankPhotoFraming {
  const w = frameRatio >= photoRatio ? 1 : frameRatio / photoRatio;
  const h = frameRatio >= photoRatio ? photoRatio / frameRatio : 1;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h, rotation: 0, ref_photo_id: refPhotoId };
}

/** A vector in photo pixels (H = 1, W = photoRatio) turned by `degrees`. */
function turn(dx: number, dy: number, degrees: number): [number, number] {
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [dx * cos - dy * sin, dx * sin + dy * cos];
}

/** Keeps the size within bounds (shape unchanged), the centre on the photo and the rotation in range. */
export function clampFraming(framing: TankPhotoFraming): TankPhotoFraming {
  const scale = clamp(
    1,
    Math.max(MIN_SIZE / framing.w, MIN_SIZE / framing.h),
    Math.min(MAX_SIZE / framing.w, MAX_SIZE / framing.h)
  );
  const w = framing.w * scale;
  const h = framing.h * scale;
  const centreX = framing.x + framing.w / 2;
  const centreY = framing.y + framing.h / 2;
  const cx = clamp(centreX, 0, 1);
  const cy = clamp(centreY, 0, 1);
  // Unchanged values stay exactly as they were (no float drift through the centre)
  const keepX = scale === 1 && cx === centreX;
  const keepY = scale === 1 && cy === centreY;
  return {
    ...framing,
    x: keepX ? framing.x : cx - w / 2,
    y: keepY ? framing.y : cy - h / 2,
    w,
    h,
    rotation: clamp(framing.rotation, -MAX_ROTATION, MAX_ROTATION),
  };
}

/**
 * The framing given the frame's shape, keeping its centre, width and rotation:
 * for a saved framing whose shape no longer matches the reference's (the cover
 * changed to a photo of another shape).
 */
export function withAspect(
  framing: TankPhotoFraming,
  photoRatio: number,
  frameRatio: number
): TankPhotoFraming {
  const h = (framing.w * photoRatio) / frameRatio;
  const cy = framing.y + framing.h / 2;
  return clampFraming({ ...framing, y: cy - h / 2, h });
}

/** Drag the shot by (dx, dy), in fractions of the frame's size: the photo follows the pointer. */
export function panBy(
  framing: TankPhotoFraming,
  dx: number,
  dy: number,
  photoRatio: number
): TankPhotoFraming {
  const [px, py] = turn(dx * framing.w * photoRatio, dy * framing.h, -framing.rotation);
  return clampFraming({ ...framing, x: framing.x - px / photoRatio, y: framing.y - py });
}

/** Zoom by `factor` (above 1 is closer) keeping the frame point (u, v) still. */
export function zoomAbout(
  framing: TankPhotoFraming,
  factor: number,
  u: number,
  v: number,
  photoRatio: number
): TankPhotoFraming {
  const target = clampFraming({ ...framing, w: framing.w / factor, h: framing.h / factor });
  // The photo point under (u, v), from the old centre and from the new one, in photo pixels
  const [ax, ay] = turn(
    (u - 0.5) * framing.w * photoRatio,
    (v - 0.5) * framing.h,
    -framing.rotation
  );
  const [bx, by] = turn((u - 0.5) * target.w * photoRatio, (v - 0.5) * target.h, -framing.rotation);
  const cx = framing.x + framing.w / 2 + (ax - bx) / photoRatio;
  const cy = framing.y + framing.h / 2 + (ay - by);
  return clampFraming({ ...target, x: cx - target.w / 2, y: cy - target.h / 2 });
}

/** Turn the shot to `degrees`, about the frame's centre. */
export function rotateTo(framing: TankPhotoFraming, degrees: number): TankPhotoFraming {
  return clampFraming({ ...framing, rotation: degrees });
}

/** The zoom shown on the editor's slider: 1 at the starting fit, 2 twice as close. */
export function zoomOf(framing: TankPhotoFraming, fit: TankPhotoFraming): number {
  return fit.w / framing.w;
}

/** The photo point (in fractions of its width and height) that the frame point (u, v) shows. */
export function photoPointAt(
  framing: TankPhotoFraming,
  u: number,
  v: number,
  photoRatio: number
): [number, number] {
  const [px, py] = turn(
    (u - 0.5) * framing.w * photoRatio,
    (v - 0.5) * framing.h,
    -framing.rotation
  );
  return [framing.x + framing.w / 2 + px / photoRatio, framing.y + framing.h / 2 + py];
}

/** Rounded as the server stores it (4 places), with the reference it was lined up against. */
export function forSaving(framing: TankPhotoFraming, refPhotoId: number | null): TankPhotoFraming {
  const w = round(framing.w);
  const h = round(framing.h);
  return {
    x: onPhoto(round(framing.x), w),
    y: onPhoto(round(framing.y), h),
    w,
    h,
    rotation: round(framing.rotation),
    ref_photo_id: refPhotoId,
  };
}

/**
 * A rounded start whose crop centre (start + size / 2) is on the photo, as the
 * server checks it: rounding start and size apart can carry a centre that sat
 * on an edge a hair past it, so the start moves back by one step of 0.0001.
 */
function onPhoto(start: number, size: number): number {
  if (start + size / 2 < 0) return round(start + 1 / DECIMALS);
  if (start + size / 2 > 1) return round(start - 1 / DECIMALS);
  return start;
}
